import React, { useEffect, useRef, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Progress,
  Row,
  UncontrolledTooltip,
} from "reactstrap"
import PropTypes from "prop-types"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import { Table as AntdTable } from "antd"
import { defaultPageSizeOptions, downloadExcel, exportPDF, showToast, stringSorter } from "utils/general"
import { tableDefaultHeight } from "constants/layout"
import { addOrganizationMember, addRealmRole } from "helpers/keycloak_api_helper"

const KeycloakTools = props => {
  const [usernames, setUsernames] = useState("")
  const [data, setData] = useState([])
  const [updateCounter, setUpdateCounter] = useState(0)
  const [ongoingAction, setOngoingAction] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [menu, setMenu] = useState(false)
  const [title] = useState(props.t("Keycloak Tools"))

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  // Parse users string to array
  const parseUsers = () => {
    try {
      let arr = usernames.split(",")
      let temp = []
      let counter = 0;
      for (const element of arr) {
        if (element.trim().length > 0) {
          temp.push({ "username": element.trim(), "status": "", "status2": "", "id": counter++ })
        }
      }
      setData(temp)
      setUpdateCounter(0)
      setSelectedRowKeys([])
    }
    catch (e) {
      console.log(e)
    }
  }

  // Set role to users
  const setRoles = async () => {
    if (data?.length > 0 && !ongoingAction) {
      setOngoingAction(true)
      setUpdateCounter(0)

      // Filter selected
      const filterData = data.filter(d => selectedRowKeys.includes(d.id))
      for (let element of filterData) {
        let res = await setScimManaged(element.username)
        console.log(element.username, res, element.id)

        const newData = data.map((c, i) => {
          if (c["id"] === element.id) {
            c['status'] = res
            return c;
          } else {
            return c;
          }
        });
        setData(newData);
        setUpdateCounter(prev => prev + 1)
      }
      setOngoingAction(false)
    }
  }

  // Keycloak API request - set scim_managed role
  const setScimManaged = async (username) => {
    return addRealmRole(abortRef.current, username).then(result => {
      return result
    })
      .catch(err => {
        console.log(err)
        return "" + err
      })
  }

  // Add members to org
  const addOrgMembers = async () => {
    if (data?.length > 0 && !ongoingAction) {
      setOngoingAction(true)
      setUpdateCounter(0)

      // Filter selected
      const filterData = data.filter(d => selectedRowKeys.includes(d.id))
      for (let element of filterData) {
        let res = await addMember(element.username)
        console.log(element.username, res, element.id)

        const newData = data.map((c, i) => {
          if (c["id"] === element.id) {
            c['status2'] = res
            return c;
          } else {
            return c;
          }
        });
        setData(newData);
        setUpdateCounter(prev => prev + 1)
      }
      setOngoingAction(false)
    }
  }

  // Keycloak API request - add organization member
  const addMember = async (username) => {
    return addOrganizationMember(abortRef.current, username).then(result => {
      return result
    })
      .catch(err => {
        console.log(err)
        return "" + err
      })
  }

  // Export data
  const exportData = (type = "excel") => {
    const heading = [
      props.t("Username"),
      props.t("Status"),
      props.t("Status") + " 2",
    ]
    const exportData = data.map(elt => [
      elt.username,
      elt.status,
      elt.status2,
    ])

    if (type == "excel") {
      downloadExcel(title, [heading], exportData)
      showToast(props.t("Excel file exported succesfully!"), "success")
    } else if (type == "pdf") {
      exportPDF(title, heading, exportData)
      showToast(props.t("PDF file exported succesfully!"), "success")
    }
  }

  // Table selections
  const rowSelection = {
    selectedRowKeys,
    onSelect: (row, isSelect, rowArray) => {
      if (!ongoingAction) {
        setUpdateCounter(0)
        if (isSelect) {
          setSelectedRowKeys(prevState => [...prevState, row.id])
        } else {
          setSelectedRowKeys(prevState =>
            prevState.filter(prevItem => prevItem !== row.id)
          )
        }
      }
    },
    onSelectAll: (isSelect, object, rowArray) => {
      if (!ongoingAction) {
        setUpdateCounter(0)
        if (isSelect) {
          rowArray.map(row =>
            setSelectedRowKeys(prevState => [...prevState, row.id])
          )
        } else {
          setSelectedRowKeys([])
        }
      }
    },
  }

  // Table structure
  const columns = [
    {
      dataIndex: "username",
      title: props.t("Username"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "username", sortOrder)
      },
      defaultSortOrder: "ascend",
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "status",
      title: props.t("Role result"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "status", sortOrder)
      },
      width: "300px",
      ellipsis: true,
    },
    {
      dataIndex: "status2",
      title: props.t("Organization result"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "status2", sortOrder)
      },
      width: "300px",
      ellipsis: true,
    },
  ]

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={props.t("Keycloak tools")} />
          <Card>
            <CardBody>
              <FormGroup>
                <Label>{props.t("Usernames CSV")}</Label>
                <Input
                  name="message"
                  type="textarea"
                  rows={1}
                  value={usernames}
                  onChange={(v) => { setUsernames(v.target.value) }}
                ></Input>
              </FormGroup>
              <Row>
                <Col className="col-auto">
                  <Button
                    color="primary"
                    disabled={ongoingAction}
                    onClick={() => {
                      parseUsers()
                    }}
                  >
                    {props.t("Parse users")}
                  </Button>

                </Col>
                <Col>
                  <span className="float-end ms-2">
                    <UncontrolledTooltip
                      placement="top"
                      target="downloadstatstable"
                    >
                      {props.t("Download statistics")}
                    </UncontrolledTooltip>
                    <Dropdown
                      id="printButtonServicesStats"
                      isOpen={menu}
                      toggle={() => setMenu(!menu)}
                      className="download-btn text-center"
                      type="button"
                    >
                      <DropdownToggle
                        id="downloadstatstable"
                        className="text-muted"
                        tag="i"
                      >
                        <i className="mdi mdi-download" />
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem
                          onClick={() => exportData("excel")}
                        >
                          {props.t("Download .xlsx")}
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => exportData("pdf")}
                        >
                          {props.t("Download .pdf")}
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </span>
                  <div className="mt-2 float-end">
                    <p>
                      {data.length} {props.t("users")}
                    </p>
                  </div>
                </Col>
              </Row>

              <Collapse isOpen={selectedRowKeys.length > 0}>
                <Row className="py-2">
                  <Col className="col-auto">
                    <Button
                      color="primary"
                      disabled={data?.length == 0 || selectedRowKeys.length == 0 || ongoingAction}
                      onClick={() => {
                        setRoles()
                      }}
                    >
                      {props.t("scim-managed")}
                    </Button>
                    <Button
                      color="primary"
                      className="ms-2"
                      disabled={data?.length == 0 || selectedRowKeys.length == 0 || ongoingAction}
                      onClick={() => {
                        addOrgMembers()
                      }}
                    >
                      {props.t("PSHVA org")}
                    </Button>
                  </Col>
                  <Col className="col-auto" style={{ alignContent: "center" }}>
                    <span>
                      {selectedRowKeys.length +
                        " " +
                        props.t("selected users")}
                    </span>
                    <Button
                      color="danger"
                      outline
                      size="sm"
                      className="ms-2"
                      disabled={ongoingAction}
                      onClick={() => {
                        setSelectedRowKeys([])
                      }}
                    >
                      {props.t("Clear selections")}
                    </Button>
                  </Col>
                  <Col>
                    <Progress
                      color="secondary"
                      className="mt-2"
                      style={{
                        height: '24px'
                      }}
                      value={updateCounter}
                      max={selectedRowKeys.length}
                    >
                      {updateCounter + "/" + selectedRowKeys.length}
                    </Progress>
                  </Col>
                </Row>
              </Collapse>

              <Row className="mt-2">
                <Col>
                  <AntdTable
                    className="colored-header narrow-header"
                    bordered
                    size="small"
                    rowKey={"id"}
                    columns={columns}
                    dataSource={data}
                    rowSelection={rowSelection}
                    pagination={{
                      showSizeChanger: true,
                      defaultPageSize: 50,
                      pageSizeOptions: defaultPageSizeOptions,
                    }}
                    //loading={ongoingSearch}
                    tableLayout="auto"
                    scroll={{
                      x: "max-content",
                      y: tableDefaultHeight,
                    }}
                  />
                </Col>
              </Row>

            </CardBody>

          </Card>
        </Container>
      </div>
    </>
  )
}

KeycloakTools.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(KeycloakTools)